import { useEffect, useState } from "react";

const SEARCH_API_URL = 'https://data.education.gouv.fr/api/explore/v2.1/catalog/datasets/fr-en-annuaire-education/records';

/**
 * hook that fetches, from the official "Annuaire de l'éducation" API (data.education.gouv.fr),
 * the establishment associated with a UAI.
 * Pass null/undefined to skip the lookup (e.g. while the UAI is being edited or is invalid).
 *
 * @param {string|null} uai UAI number (already validated)
 * @returns {{ isLoading: boolean, establishment: object|null, error: string|null }}
 *   establishment: { name, type, status, city, isClosing }
 */
const useUaiEstablishment = (uai) => {
    const [state, setState] = useState({ isLoading: false, establishment: null, error: null });

    useEffect(() => {
        // The API lookup is case sensitive: UAI letters are stored uppercase
        const code = uai?.replace(/\s/g, '').toUpperCase();
        if (!code) {
            setState({ isLoading: false, establishment: null, error: null });
            return;
        }

        // Ignore responses for a UAI that is no longer the current one
        let ignore = false;
        setState({ isLoading: true, establishment: null, error: null });

        const params = new URLSearchParams({ where: `identifiant_de_l_etablissement="${code}"`, limit: 1 });
        fetch(`${SEARCH_API_URL}?${params}`)
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then(data => {
                if (ignore) return;
                const result = data?.results?.[0];
                if (!result) {
                    setState({ isLoading: false, establishment: null, error: "Pas d'établissement trouvé" });
                    return;
                }
                setState({
                    isLoading: false,
                    establishment: {
                        name: result.nom_etablissement,
                        type: result.type_etablissement,
                        status: result.statut_public_prive,
                        city: result.nom_commune,
                        isClosing: result.etat === 'A FERMER',
                    },
                    error: null,
                });
            })
            .catch(() => {
                if (ignore) return;
                setState({ isLoading: false, establishment: null, error: "Erreur d'appel API éducation" });
            });

        return () => { ignore = true; };
    }, [uai]);

    return state;
};

export default useUaiEstablishment;
